import { getPartnerDisplay } from '@sr/utils';
import {
  type LocationsById,
  type PartnerResItem,
  type PartnerTreeNodeV2,
  type StenaPermissions,
} from '@sr/types';

export const getDwkeysFromPartnerTrees = (partnerTrees: PartnerTreeNodeV2[]): string[] => {
  // Recursively get all dwKeys from partnerTrees
  const allDwKeys: string[] = [];

  const getDwKeys = (partnerTrees: PartnerTreeNodeV2[]) => {
    partnerTrees.forEach((partnerTree) => {
      if (partnerTree.item.dwKey.length) {
        allDwKeys.push(partnerTree.item.dwKey);
      }
      if (partnerTree.children.length) {
        getDwKeys(partnerTree.children);
      }
    });
  };

  getDwKeys(partnerTrees);

  return allDwKeys;
};

export const filterPartnersTreeOnService = ({
  serviceName,
  partnerTrees,
  permissions,
}: {
  partnerTrees: PartnerTreeNodeV2[];
  serviceName: string;
  permissions: StenaPermissions;
}): PartnerTreeNodeV2[] => {
  const allPartnerIdsInService = permissions.businessPartners
    .filter((bp) => bp.services.some((s) => s.name === serviceName))
    .flatMap((bp) => [bp.id, ...(bp.expandedPartners ?? [])]);

  return partnerTrees.filter((partnerTree) => {
    return allPartnerIdsInService.includes(partnerTree.item.dwKey);
  });
};

export const filterPartnerTreesOnAdminAccess = ({
  partnerTrees,
  permissions,
}: {
  partnerTrees: PartnerTreeNodeV2[];
  permissions: StenaPermissions;
}): PartnerTreeNodeV2[] => {
  const allAdminRootPartnerIds = permissions.businessPartners
    .filter((bp) => bp.services.some((s) => s.userManagement))
    .map((bp) => bp.id);

  return partnerTrees.filter((partnerTree) => {
    return allAdminRootPartnerIds.includes(partnerTree.item.dwKey);
  });
};

export const filterPartnerTreesOnRnpRoleAccess = ({
  partnerTrees,
  permissions,
}: {
  partnerTrees: PartnerTreeNodeV2[];
  permissions: StenaPermissions;
}): PartnerTreeNodeV2[] => {
  if (permissions.superUser) {
    return partnerTrees;
  }

  const rnpRolePartnerIds = new Set(
    permissions.businessPartners
      .filter((bp) => bp.services.some((s) => s.requestNewLocations))
      .flatMap((bp) => [bp.id, ...(bp.expandedPartners ?? [])]),
  );

  return partnerTrees.filter((partnerTree) => rnpRolePartnerIds.has(partnerTree.item.dwKey));
};

export const filterPartnersTreeOnConfidential = ({
  partnerTrees,
}: {
  partnerTrees: PartnerTreeNodeV2[];
}): PartnerTreeNodeV2[] => {
  return partnerTrees.filter((partnerTree) => {
    return !partnerTree.item.dwKey.startsWith('90');
  });
};

export const makePartnerLabels = (partner: PartnerResItem) => {
  const disp = getPartnerDisplay(partner);

  return {
    description: disp.addressAndId,
    label: disp.nameAndText,
  };
};

export const getLocationsById = (partnerTrees: PartnerTreeNodeV2[]) => {
  const locationsById: Record<string, PartnerResItem | undefined> = {};

  const addRecursive = (node: PartnerTreeNodeV2) => {
    locationsById[node.item.dwKey] = node.item;
    if (node.children.length > 0) {
      for (const child of node.children) {
        addRecursive(child);
      }
    }
  };

  partnerTrees.forEach((partnerTree) => {
    addRecursive(partnerTree);
  });

  return locationsById;
};

export const rnpAccessFilter = (partnerTrees: PartnerTreeNodeV2[]) => {
  const result: PartnerTreeNodeV2[] = [];

  const checkNode = (node: PartnerTreeNodeV2): boolean => {
    if (node.item.hasRnpAccess) {
      result.push(node);

      return true;
    }

    let hasChildWithAccess = false;

    if (node.children.length > 0) {
      for (const child of node.children) {
        if (checkNode(child)) {
          hasChildWithAccess = true;
        }
      }
    }

    return hasChildWithAccess;
  };

  // Check each top-level partner tree
  for (const tree of partnerTrees) {
    checkNode(tree);
  }

  return result;
};

export const rnpAccessFilterWithParents = (partnerTrees: PartnerTreeNodeV2[]) => {
  const processNode = (node: PartnerTreeNodeV2): PartnerTreeNodeV2[] => {
    const processedChildren = node.children.flatMap((child) => processNode(child));

    const qualifies =
      node.item.hasRnpAccess ||
      node.children.some(
        (child) =>
          child.item.hasRnpAccess ||
          child.children.some((grandchild) => grandchild.item.hasRnpAccess),
      );

    if (!qualifies) {
      return processedChildren;
    }

    return [
      {
        ...node,
        children: processedChildren,
      },
    ];
  };

  return partnerTrees.flatMap((tree) => processNode(tree));
};

export const findPartner = (locationsById: LocationsById, key: string) => {
  const partner = locationsById[key];

  if (partner) {
    return partner;
  }

  const baseKey = key.slice(0, key.lastIndexOf('_'));

  return locationsById[`${baseKey}_VENDOR`] ?? locationsById[`${baseKey}_CUSTOMER`];
};
